import moment from 'moment';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import {
  indicatorValRankingFormat,
  rankRankingFormat,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';
import { METRICS_INFOS } from '../constants';
import CommonEN from '@intelligence/_locales/en-US/common';
import CommonCN from '@intelligence/_locales/zh-CN/common';


const getRevenueAndDownloadsMetricData = (data: any): any[] => {
  const result: any[] = [];
  if (Object.prototype.toString.call(data) !== '[object Object]') return result;
  const { company_metric: companySevMetrics } = data;
  if (!companySevMetrics) return result;
  const sevMetricKeys = Object.keys(companySevMetrics);
  for (const sevMetricKey of sevMetricKeys) {
    // 纯downloads、revenue字符传，不带任何多余的字符的
    const metricKey = sevMetricKey?.split('_')?.[0];
    if (!metricKey) continue;
    const metricValueKey = `${metricKey}_metric`;
    const metricRankKey = `${metricKey}_rank`;
    const metricRankGrowthRatioKey = `${metricKey}_rank_growth_ratio`;

    const metricValueData = data?.[metricValueKey];
    const metricRankData = data?.rank;

    const metricHistoriesData = companySevMetrics?.[sevMetricKey]?.map((item: any) => ({
      date: item?.date ?? '',
      value: item?.[metricValueKey] ?? Number.NaN,
    }));

    result.push({
      metric: metricKey,
      History: metricHistoriesData ?? [],
      value: metricValueData?.local_metric ?? Number.NaN,
      value_dod: metricValueData?.local_growth_ratio ?? Number.NaN,
      rank: metricRankData?.[metricRankKey] ?? Number.NaN,
      rank_dod: metricRankData?.[metricRankGrowthRatioKey] ?? Number.NaN,
      region_type: '',
      regions: [],
      start_date: data?.start_date ?? '',
      end_date: data?.end_date ?? '',
    });
  }
  return result;
};

export const analysisSummaryRes = (summaryRes: any): any[] => {
  const revenueAndDownloadsMetricsData = getRevenueAndDownloadsMetricData(summaryRes);
  const othersMetricsData = summaryRes?.summary_list ?? [];
  return [...revenueAndDownloadsMetricsData, ...othersMetricsData];
};

const getFormattedDate = (dateType: 'day' | 'week' | 'month', date: string): string => {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  return dateType === 'month' ? m.format('YYYY-MM') : m.format('YYYY-MM-DD');
};

const getFormattedLatestDate = (dateType: 'day' | 'week' | 'month', startDate: string, endDate: string): string => {
  const formattedStartDate = getFormattedDate(dateType, startDate);
  const formattedEndDate = getFormattedDate(dateType, endDate);
  return dateType === 'week' && formattedStartDate && formattedEndDate
    ? `${formattedStartDate}~${formattedEndDate}`
    : formattedEndDate ?? formattedStartDate;
};

const getWeekDates = (
  payload: any[],
  cfg: {
    dateType: 'day' | 'week' | 'month';
  },
): string[] => {
  if (!Array.isArray(payload)) return [];
  const { dateType } = cfg ?? {};
  return payload.map((item) => {
    if (dateType === 'month') return moment(item?.date).format('YYYY-MM');
    if (dateType === 'week') {
      const startDate = moment(item?.date).format('YYYY-MM-DD');
      const endDate = moment(item?.date).add(6, 'days').format('YYYY-MM-DD');
      return `${startDate}~${endDate}`;
    }
    return moment(item?.date).format('YYYY-MM-DD');
  });
};

const regionsNamesENMap: {
  [key: string]: string;
} = {
  FR: 'FR',
  DE: 'DE',
  ES: 'ES',
  UK: 'UK',
  GB: 'UK',
  US: 'US',
};
const regionsNamesCNMap: {
  [key: string]: string;
} = {
  FR: '法国',
  DE: '德国',
  ES: '西班牙',
  UK: '英国',
  GB: '英国',
  US: '美国',
};

export const getTranslatedRegionsOnlyText = (cfg: {
  isCN: boolean;
  dataSource: string;
  metricValue: number;
  regions: string[];
}): string => {
  const { isCN, dataSource, metricValue, regions } = cfg;
  if (!metricValue) return '-';
  if (dataSource === 'npd') return isCN ? CommonCN.dbi_us_only : CommonEN.dbi_us_only;
  if (!regions?.length) return '-';
  const comma = isCN ? '，' : ',';
  const regionsNamesMap = isCN ? regionsNamesCNMap : regionsNamesENMap;
  const regionsList = regions?.filter((item) => regionsNamesMap.hasOwnProperty(item?.toUpperCase() || ''));
  const regionsText = regionsList?.map((region) => {
    let regionTmp = region?.toUpperCase() || '';
    return regionsNamesMap[regionTmp];
  })?.join(comma);
  return isCN ? `仅供${regionsText}数据` : `${regionsText} data only`;
};

export const getMetricNameTips = (
  text: string,
  textCN: string,
  cfg: {
    isCN: boolean;
    dataSource: string;
    metricValue: number;
    regions: string[];
  },
): string => {
  const { isCN, dataSource, metricValue, regions } = cfg || {};
  const textTmp = isCN ? textCN : text;
  const regionsText = getTranslatedRegionsOnlyText({
    isCN,
    dataSource,
    metricValue,
    regions,
  });
  return textTmp?.replace('#{regions}', regionsText) || '';
};

export const generateSummaryCardCompData = (
  summaryData: any[],
  cfg: {
    isCN: boolean;
    dateType: 'day' | 'week' | 'month';
    startDate: string;
    endDate: string;
  },
): any[] => {
  const result: any[] = [];
  if (!Array.isArray(summaryData)) return result;
  const { isCN, dateType, startDate, endDate } = cfg ?? {};
  return summaryData?.map((item, index: number) => {
    const {
      metric,
      value: metricValue,
      value_dod: metricGrowthRatioValue,
      rank: metricRank,
      rank_dod: metricRankGrowthValue,
      regions,
      start_date: startDateInner,
      end_date: endDateInner,
    } = item ?? {};
    const metricInfo = METRICS_INFOS?.[metric];
    const { name: metricName, nameTips: metricNameTips, nameTipsCN: metricNameTipsCN, dataSource, entityType, segment, subSegment } = metricInfo ?? {};
    const formattedMetricValue = newMathFormat(metricValue);
    const { _indicatorContent, _indicatorSize, _indicatorColor, _isIndicatorArrow, _indicatprsArrowColor } =
      indicatorValRankingFormat(metricGrowthRatioValue);
    const { _rankContent, _rankColor, _rankSize, _isRankArrow, _rankArrowColor } = rankRankingFormat(
      metricRankGrowthValue?.toString(10),
    );
    const dates = item?.History?.map((subItem: any) => subItem?.date) ?? [];
    const datesValues = item?.History?.map((subItem: any) => subItem?.value) ?? [];
    const lineDomKey = `line${index}`;
    const weekDates = getWeekDates(item?.History, cfg) ?? [];
    const formattedLatestDate = getFormattedLatestDate(dateType, startDateInner, endDateInner);

    return {
      titleContent: metricName,
      titleIcon: dataSource,
      data_source: dataSource,
      baseIndicators: {
        indicatorsVal: formattedMetricValue,
        rankVal: metricRank ?? Number.NaN,
        latestDate: formattedLatestDate ?? '',
      },
      numArrowData: {
        numContent: _indicatorContent,
        numColor: _indicatorColor,
        numSize: _indicatorSize,
        arrow: _isIndicatorArrow,
        arrowColor: _indicatprsArrowColor,
      },
      lineData: {
        lineDom: lineDomKey,
        dataType: dateType ?? 'day',
        weekDate: weekDates,
        xAxisData: dates,
        seriesData: datesValues,
        predictData: [false, false, false, false, false, false, false],
      },
      lineDom: lineDomKey,
      rankData: {
        numContent: _rankContent,
        numColor: _rankColor,
        numSize: _rankSize,
        arrow: _isRankArrow,
        arrowColor: _rankArrowColor,
      },
      index,
      moduleName: 'companies',
      isPolishing: false,
      entityType: entityType,
      segment: segment,
      subSegment: subSegment,
      conditionDate: dateType ?? 'day',
      cardItem: {
        titleContent: metricName,
        titleIcon: dataSource,
        data_source: dataSource,
        baseIndicators: {
          indicatorsVal: formattedMetricValue,
          rankVal: metricRank ?? Number.NaN,
          latestDate: formattedLatestDate ?? '',
        },
        numArrowData: {
          numContent: _indicatorContent,
          numColor: _indicatorColor,
          numSize: _indicatorSize,
          arrow: _isIndicatorArrow,
          arrowColor: _indicatprsArrowColor,
        },
        rankData: {
          numContent: _rankContent,
          numColor: _rankColor,
          numSize: _rankSize,
          arrow: _isRankArrow,
          arrowColor: _rankArrowColor,
        },
        lineData: {
          lineDom: lineDomKey,
          dataType: dateType ?? 'day',
          weekDate: weekDates,
          xAxisData: dates,
          seriesData: datesValues,
          predictData: [false, false, false, false, false, false, false],
        },
        lineDom: lineDomKey,
        conditionDate: dateType ?? 'day',
        conditionPlatform: dataSource,
        startDate: startDate ?? '',
        endDate: endDate ?? '',
        entityType: entityType,
        segment: segment,
        subSegment: subSegment,
      },
      customBubbleContent: getMetricNameTips(metricNameTips as string, metricNameTipsCN as string, {
        isCN,
        dataSource,
        metricValue,
        regions,
      }),
    };
  });
};

